/**
 * Admin-only preference: fun meme-style loaders (localStorage + context).
 * For Media Devoted | Created By Ciro Autiero
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import { useAuth } from '@/providers/AuthProvider';

const STORAGE_KEY = 'trafficpulse-meme-loader';

function readStored(): boolean {
  if (typeof window === 'undefined') return false;
  return window.localStorage.getItem(STORAGE_KEY) === '1';
}

export interface MemeLoaderPreferenceContextValue {
  canCustomize: boolean;
  memeLoaderEnabled: boolean;
  setMemeLoaderEnabled: (next: boolean) => void;
}

const MemeLoaderPreferenceContext = createContext<
  MemeLoaderPreferenceContextValue | undefined
>(undefined);

export function MemeLoaderPreferenceProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const canCustomize = (user?.role || '').toLowerCase() === 'admin';
  const [stored, setStored] = useState(readStored);

  useEffect(() => {
    setStored(readStored());
  }, [user?.id]);

  const memeLoaderEnabled = canCustomize && stored;

  const setMemeLoaderEnabled = useCallback(
    (next: boolean) => {
      if (!canCustomize) return;
      window.localStorage.setItem(STORAGE_KEY, next ? '1' : '0');
      setStored(next);
    },
    [canCustomize],
  );

  const value = useMemo(
    (): MemeLoaderPreferenceContextValue => ({
      canCustomize,
      memeLoaderEnabled,
      setMemeLoaderEnabled,
    }),
    [canCustomize, memeLoaderEnabled, setMemeLoaderEnabled],
  );

  return (
    <MemeLoaderPreferenceContext.Provider value={value}>
      {children}
    </MemeLoaderPreferenceContext.Provider>
  );
}

export function useMemeLoaderPreference(): MemeLoaderPreferenceContextValue {
  const ctx = useContext(MemeLoaderPreferenceContext);
  if (!ctx) {
    throw new Error('useMemeLoaderPreference must be used within MemeLoaderPreferenceProvider');
  }
  return ctx;
}

/** Spinner/overlay/panel: works without provider (memes off). */
export function useMemeLoaderForUi(): { memeLoaderEnabled: boolean } {
  const ctx = useContext(MemeLoaderPreferenceContext);
  return { memeLoaderEnabled: ctx?.memeLoaderEnabled ?? false };
}
